import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { getAllBlogPosts } from "../data/blogPosts";
import useSEO from "../hooks/useSEO";

const formatDate = (dateValue) =>
  new Date(dateValue).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const BlogPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const posts = useMemo(() => getAllBlogPosts(), []);

  const filteredPosts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return posts;
    }

    return posts.filter((post) => {
      const text = `${post.title} ${post.excerpt} ${post.tags.join(" ")}`.toLowerCase();
      return text.includes(query);
    });
  }, [posts, searchQuery]);

  useSEO({
    title: "Industrial Water Treatment Blog",
    description:
      "Read practical guides on industrial RO plants, ETP, STP, and water reuse to improve compliance and operational efficiency.",
    keywords:
      "industrial water treatment blog, ro plant guide, etp maintenance tips, wastewater treatment insights",
    canonicalPath: "/blog",
    openGraphImage: "/banners/banner-1.png",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "VIVID H2O Solutions Blog",
      description:
        "Industrial water treatment articles, maintenance guides, and compliance insights.",
      url: `${window.location.origin}/blog`,
    },
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blog-page">
      <section className="bg-gradient-to-r from-[#0d47a1] to-[#1976d2] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-4">
            VIVID Blog
          </h1>
          <p className="text-blue-100 max-w-2xl mx-auto">
            Weekly and monthly insights on RO, ETP, STP, ZLD, and industrial water
            management.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm p-5 mb-8">
            <div className="relative max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by topic, keyword, or tag"
                className="pl-10 border-gray-200 focus:border-[#0d47a1] focus:ring-[#0d47a1]"
                data-testid="blog-search"
              />
            </div>
          </div>

          {filteredPosts.length === 0 ? (
            <div className="bg-white rounded-xl p-10 text-center text-gray-600">
              No blog posts found for this search.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {filteredPosts.map((post) => (
                <Card key={post.slug} className="overflow-hidden border-0 shadow-md bg-white">
                  <div className="aspect-video overflow-hidden bg-gray-100">
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <CardContent className="p-5">
                    <div className="text-xs text-gray-500 mb-2">
                      {formatDate(post.publishedAt)} • {post.readTime}
                    </div>
                    <h2 className="font-heading font-semibold text-lg text-gray-900 mb-2 line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-[#0d47a1]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-[#0d47a1] font-medium hover:text-[#0a3d8f]"
                    >
                      Read article <ArrowRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
